import React, {
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Reports from "../../common/reports/Reports";
import GlobalContext from "../../framework/GlobalContext";
import classes from "./DiscountSlabReport.module.css";
import DiscountSlabReportFilter from "./DiscountSlabReportFilter";
import Button from "../../framework/Button";
import Shortcut from "../../framework/Shortcut";
import DiscountSlabDetails from "./discountSlabDetails";
import Autocomplete from "../../framework/Autocomplete";
import DiscountSlabReportSummary from "./DiscountSlabReportSummary";
import AddDiscountSlab from "./addDiscountSlab";
import { addDiscountProgram } from "../../services/discount-program";
import { displayToast } from "../../framework/toast/toastUtils";
import dayjs from "dayjs";

const DiscountSlabReport = () => {
  const [onFilterShow, setOnFilterShow] = useState(false);
  const [onColumnShow, setOnColumnShow] = useState(false);
  const [rowLevelCTAs, setRowLevelCTAs] = useState([]);
  const [filterParams, setFilterParams] = useState(null);
  const [filterStates, setFilterStates] = useState(null);
  const [currentRowData, setCurrentRowData] = useState(null);
  const [showDiscountSlabDetails, setShowDiscountSlabDetails] = useState(false);
  const [showAddDiscountSlab, setShowAddDiscountSlab] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const { currentComponent, setCurrentComponent } = useContext(GlobalContext);
  const reportRef = useRef();
  const refreshRef = useRef();
  const editRef = useRef();
  const enterRef = useRef();
  const inactivateBtnRef = useRef();
  const exportRef = useRef();
  const addBtnRef = useRef();

  const refreshData = useCallback((filterData = {}) => {
    reportRef.current.applyFilters(filterData);
  }, []);

  const applyFilterHandler = useCallback((params, filterStates) => {
    setFilterParams(params);
    setFilterStates(filterStates);
  }, []);
  const quickExportHandler = () => {
    reportRef?.current?.quickExport({ fileName: "DiscountSlabReport" });
  };
  const closeDiscountSlabDetails = useCallback(() => {
    setShowDiscountSlabDetails(false);
    setCurrentComponent("grid_reports");
  }, [setCurrentComponent]);

  const closeAddDiscountSlab = useCallback(() => {
    setIsEdit(false);
    setShowAddDiscountSlab(false);
    setCurrentComponent("grid_reports");
  }, [setCurrentComponent]);

  const handleIsActive = useCallback(async () => {
    const programDetails = currentRowData?.data;
    const discountDetails = programDetails.discountProgramDetails.map(
      (details) => ({
        ...details,
        deleted: programDetails.deleted ? false : true,
      })
    );
    const payload = {
      ...programDetails,
      deleted: programDetails?.deleted ? false : true,
      discountProgramDetails: discountDetails,
    };
    await addDiscountProgram(payload).then((resp) => {
      if (resp?.status === "success") {
        displayToast({
          type: "success",
          title: programDetails?.deleted
            ? "Discount Program Activated!"
            : "Discount Program Inactivated!",
        });
        refreshData();
      }
    });
  }, [currentRowData?.data, refreshData]);

  const listener = useCallback((e) => {
    const key = e.key?.toLowerCase();
    const ctrlKey = e.ctrlKey;
    const altKey = e.altKey;
    if (altKey && key === "r") {
      refreshRef.current.click();
      return;
    }
    if (ctrlKey && key === "e") {
      editRef.current.click();
      return;
    }
    if (ctrlKey && key === "i") {
      enterRef.current?.click();
      return;
    }
    if (key === "enter") {
      enterRef.current.click();
      return;
    }
    if (ctrlKey && key === "d") {
      setIsEdit(false);
      addBtnRef.current.click();
      return;
    }
    if (altKey && key === "l") {
      inactivateBtnRef.current.click();
      return;
    }
  }, []);
  useEffect(() => {
    const allRowCTAs = [
      <Button
        ref={editRef}
        startIcon={
          <>
            <Shortcut keys={["Ctrl", "E"]} />
          </>
        }
        label="Edit"
        onClick={() => {
          setIsEdit(true);
          setShowAddDiscountSlab(true);
        }}
        isAccessible={currentRowData && false}
      />,
      <Button
        ref={enterRef}
        startIcon={
          <>
            <Shortcut keys={["Enter"]} />/<Shortcut keys={["Ctrl", "I"]} />
          </>
        }
        label="info"
        onClick={() => {
          setShowDiscountSlabDetails(true);
        }}
        isAccessible={currentRowData}
      />,
      <Button
        ref={inactivateBtnRef}
        startIcon={
          <>
            <Shortcut keys={["Alt", "L"]} />
          </>
        }
        label={currentRowData?.data?.deleted ? "Activate" : "Inactivate"}
        onClick={() => {
          handleIsActive();
        }}
        isAccessible={
          currentRowData &&
          dayjs(currentRowData?.data?.endDate).isAfter(dayjs(), "day")
        }
        sx={{
          width: "10.3125rem",
        }}
      />,
    ];
    setRowLevelCTAs(allRowCTAs);
  }, [currentRowData, handleIsActive]);

  useEffect(() => {
    setCurrentComponent("grid_reports");
  }, [setCurrentComponent]);

  useEffect(() => {
    if (!onFilterShow && !onColumnShow && currentComponent === "grid_reports") {
      document.addEventListener("keyup", listener, false);
    }
    return () => {
      document.removeEventListener("keyup", listener, false);
    };
  }, [currentComponent, listener, onColumnShow, onFilterShow]);
  useEffect(() => {
    refreshData({ isFilterChanged: true, ...filterStates });
  }, [refreshData, filterStates]);
  return (
    <div className={classes["discount-slab-report-container"]}>
      <Reports
        identifier="discountSlabReport"
        reportRef={reportRef}
        rowLevelCTAs={rowLevelCTAs}
        filterStates={filterStates}
        filterParams={filterParams}
        onFilterShowClick={setOnFilterShow}
        onColumnShowClick={setOnColumnShow}
        currentRow={(data) => {
          setCurrentRowData(data);
        }}
        onCellClicked={(params) => {
          if (params.column.colId === "discountProgramCode") {
            if (params.data.discountProgramCode)
              setShowDiscountSlabDetails(true);
          }
        }}
        filters={
          <DiscountSlabReportFilter
            reportRef={reportRef}
            filters={(filters) => {
              setFilterStates(filters);
            }}
            onFilterShow={onFilterShow}
            refreshData={refreshData}
            applyFilters={applyFilterHandler}
          />
        }
      />
      <Autocomplete
        show={showDiscountSlabDetails}
        height="60%"
        closeAutoComplete={closeDiscountSlabDetails}
      >
        <DiscountSlabDetails
          discountProgramCode={currentRowData?.data?._id}
          onEscape={closeDiscountSlabDetails}
        />
      </Autocomplete>
      <Autocomplete
        show={showAddDiscountSlab}
        height=" 80%"
        closeAutoComplete={closeAddDiscountSlab}
      >
        <AddDiscountSlab
          onEscape={closeAddDiscountSlab}
          refreshData={refreshData}
          currentRowData={currentRowData?.data}
          isEdit={isEdit}
        />
      </Autocomplete>
      <DiscountSlabReportSummary
        refreshData={refreshData}
        refreshRef={refreshRef}
        addBtnRef={addBtnRef}
        quickExport={quickExportHandler}
        exportRef={exportRef}
        setShowAddDiscountSlab={setShowAddDiscountSlab}
        setIsEdit={setIsEdit}
      />
    </div>
  );
};

export default DiscountSlabReport;
